import {useState, useEffect, useRef} from 'react'

import Style from './style'
import addIcon from './asset/add.svg'
import eraseIcon from 'shared/assets/erase.svg'
import upperIcon from './asset/upper.svg'
import downIcon from './asset/down.svg'
import SeriesModal from './ui/SeriesModal'
import useDurationEvent from './model/useDurationEvent'
import useReEntryChipEvent from './model/useReEntryChipEvent'

import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	verticalListSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import dragIcon from 'shared/assets/drag-icon.svg'
import removeIcon from 'shared/assets/remove-icon.svg'

import MainHeader from 'widget/MainHeader'

import useGetEventDetail from 'entity/event/useGetEventDetail'
import useGetSeedTicketTypeList from 'entity/series/useGetSeedTicketTypeList'
import usePostImgListUpload from 'entity/util/usePostImgListUpload'
import usePostImgUpload from 'entity/util/usePostImgUpload'

import usePutEvent from 'entity/event/usePutEvent'

import CStyle from 'shared/style/commonStyle'
import ITStyle from 'shared/style/inputTableStyle'
import DefaultButton from 'shared/ui/DefaultButton'
import timestampCalculator from 'shared/lib/timestampCalculator'
import {
	OFFICIAL,
	PRELIMINARY,
	STORE,
	TRYOUT,
	FREEROLL,
} from 'shared/constant/lookup/eventType'
import {
	ROLE_STORE_MANAGER,
	ROLE_FRANCHISE_MANAGER,
} from 'shared/constant/lookup/role'
import useRoleCookie from 'shared/model/useRoleCookie'
import {datetimeLocalValueCalculator} from 'shared/lib/datetimeLocalCalculator'
import {handleNumbersOnly, handleTextOnly} from 'shared/lib/inputValidation'
import useGetStoreTicketTypes from 'entity/store/useGetStoreTicketTypes'
import TiptapEditor from 'shared/ui/TiptapEditor'
import usePrizeEditors from 'shared/hooks/usePrizeEditors'
import PrizeHeaderControls from 'shared/ui/PrizeHeaderControls'
import {PrizeContainer, Prize} from 'shared/ui/PrizeComponents'
import usePrizeEvent from './model/usePrizeEvent'
import useGetQualificationCreateList from 'entity/exam/useGetQualificationCreateList'

// Sortable Row Component
const SortableRow = ({children, id}) => {
	const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
		useSortable({id})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		zIndex: isDragging ? 999 : 'auto',
		position: 'relative',
	}

	return (
		<tr ref={setNodeRef} style={style} {...attributes}>
			{children(listeners)}
		</tr>
	)
}

const EventEdit = () => {
	const nameRef = useRef()
	const eventTypeRef = useRef()
	const [eventTypeFlag, setEventTypeFlag] = useState(null)
	const addressRef = useRef()
	const dateRef = useRef()
	const regDateRef = useRef()
	const proGradeRef = useRef()
	const [requiredProGradeFlag, setRequiredProGradeFlag] = useState(null)
	const contentRef = useRef()
	const imgInputRef = useRef()
	const posterInputRef = useRef()
	const [sereisData, setSeriesData] = useState()
	const startingChipRef = useRef()
	const regEndLevelRef = useRef()
	const earlyEndLevelRef = useRef()
	const earlyChipRef = useRef()
	const maxEntryRef = useRef()
	const reEntryRef = useRef()
	const addOnRef = useRef()
	const addOnChipRef = useRef()
	const prizeTotalRef = useRef()
	const seeTicketTypeRef = useRef()
	const [seedTicketTypeIdx, setSeedTicketTypeIdx] = useState(null)
	const ticketNumberRef = useRef()
	const publishedTicketNumberRef = useRef()
	const voucherTypeRef = useRef()
	const durationRef = useRef()

	// Initialize prize editors hook
	const {
		prizes,
		prizeList,
		addPrize,
		removePrize,
		updatePrizeContent,
		getPrizeEditorRef,
		setPrizesFromData,
	} = usePrizeEditors()

	const [selectedVoucherType, setSelectedVoucherType] = useState(null)

	const [voucherTypeInit, setVoucherTypeInit] = useState(false)

	const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false)

	const [getEventData] = useGetEventDetail()

	const [qualificationList] = useGetQualificationCreateList()

	const [
		durationList,
		setDuration,
		createDurationEvent,
		createBreakEvent,
		deleteDurationEvent,
		insertDurationEvent,
		insertBreakEvent,
		moveDurationEvent,
	] = useDurationEvent(getEventData?.data?.duration_list)
	const [
		reEntryList,
		changeReEntryChipEvent,
		incReEntryChipEvent,
		deleteReEntryEvent,
	] = useReEntryChipEvent(getEventData?.data?.re_entry_chip)

	const [seedTicketTypeList] = useGetSeedTicketTypeList(sereisData?.idx)
	const [posterFilePath, uploadPosterEvent, deletePosterImg] = usePostImgUpload(
		getEventData?.data?.poster_img
	)
	const [imgPathList, uploadImgEvent, deleteImg] = usePostImgListUpload(
		getEventData?.data?.img_list,
		10
	)

	const [
		prizeListTable,
		setPrize,
		createPrizeEvent,
		deletePrizeEvent,
		upperPrizeEvent,
		downPrizeEvent,
	] = usePrizeEvent(getEventData?.data?.prize_list)

	const [putEvent] = usePutEvent(
		nameRef,
		eventTypeFlag,
		addressRef,
		dateRef,
		regDateRef,
		requiredProGradeFlag,
		contentRef,
		imgPathList,
		posterFilePath,
		sereisData?.idx,
		startingChipRef,
		regEndLevelRef,
		earlyEndLevelRef,
		earlyChipRef,
		maxEntryRef,
		reEntryRef,
		reEntryList,
		addOnRef,
		addOnChipRef,
		prizeTotalRef,
		seedTicketTypeIdx,
		ticketNumberRef,
		publishedTicketNumberRef,
		prizeList,
		prizeListTable,
		durationList.map((item) => item.data),
		selectedVoucherType,
		durationRef
	)

	const [roleCookie] = useRoleCookie()
	const [ticketTypes] = useGetStoreTicketTypes()

	// DnD Sensors and Handlers
	const isDraggingRef = useRef(false)

	const handleDragStart = () => {
		isDraggingRef.current = true
	}

	const handleDragEnd = (event) => {
		const {active, over} = event

		// Slight delay to allow any pending click events to be ignored
		setTimeout(() => {
			isDraggingRef.current = false
		}, 300)

		if (over && active.id !== over.id) {
			const oldIndex = durationList.findIndex((item) => item.id === active.id)
			const newIndex = durationList.findIndex((item) => item.id === over.id)
			if (oldIndex !== -1 && newIndex !== -1) {
				moveDurationEvent(oldIndex, newIndex)
			}
		}
	}

	// 대회 상세보기 요청 후, 대회 종류 Select와 Series 데이터 삽입
	useEffect(() => {
		// 대회 정보 통신 후 실행
		if (getEventData) {
			if (eventTypeRef.current) {
				eventTypeRef.current.value = getEventData?.data?.event_type_flag
				setEventTypeFlag(getEventData?.data?.event_type_flag)
			}
			setSeriesData({
				idx: getEventData?.data?.series_idx,
				start_date: getEventData?.data?.series_start_date,
				end_date: getEventData?.data?.series_end_date,
				name: getEventData?.data?.series_name,
			})

			// Initialize prize editors with existing data
			if (getEventData?.data?.prize_editor) {
				setPrizesFromData(getEventData.data.prize_editor)
			}

			if (getEventData?.data?.store_ticket_type_idx) {
				setSelectedVoucherType(getEventData.data.store_ticket_type_idx)
				setVoucherTypeInit(true)
			} else {
				setSelectedVoucherType('init')
				setVoucherTypeInit(true)
			}
		}
	}, [getEventData, setPrizesFromData])

	const [proGradeInitialized, setProGradeInitialized] = useState(false)

	// 요구 프로 자격 Select 삽입
	useEffect(() => {
		if (eventTypeFlag && !proGradeInitialized && qualificationList) {
			// 대회 종류가 삽입되었을 때 실행해야 버그 없음
			if (eventTypeFlag === STORE) {
				setRequiredProGradeFlag(1)
			} else {
				if (proGradeRef.current) {
					proGradeRef.current.value = getEventData?.data?.pro_grade_flag
				}
				setRequiredProGradeFlag(getEventData?.data?.pro_grade_flag)
			}
			setProGradeInitialized(true)
		}
	}, [eventTypeFlag, qualificationList, getEventData, proGradeInitialized])

	// 시드권 종류 목록 요청 후, 시드권 종류 Select 데이터 삽입
	useEffect(() => {
		if (seedTicketTypeList) {
			if (seeTicketTypeRef.current) {
				seeTicketTypeRef.current.value = `${getEventData?.data?.seed_ticket_type_idx}`
				setSeedTicketTypeIdx(getEventData?.data?.seed_ticket_type_idx)
			}
		}
	}, [seedTicketTypeList])

	const previewEvent = () => {
		const eventData = {
			name: nameRef.current?.value,
			address: addressRef.current?.value,
			event_type_flag: eventTypeFlag,
			date: dateRef.current?.value,
			reg_date: regDateRef.current?.value,
			content: contentRef.current?.value,
			starting_chip: startingChipRef.current?.value,
			reg_end_level: regEndLevelRef.current?.value,
			early_end_level: earlyEndLevelRef.current?.value,
			early_chip: earlyChipRef.current?.value,
			add_on: addOnRef.current?.value,
			add_on_chip: addOnChipRef.current?.value,
			re_entry: reEntryRef.current?.value,
			max_entry: maxEntryRef.current?.value,
			prize_total: prizeTotalRef.current?.value,
			ticket_number: ticketNumberRef.current?.value,

			// Mocking structure for lists
			prize_editor: prizes,
			prize_list: prizeListTable,
			duration_list: durationList.map((item) => item.data),

			// Files (can't really pass files, but pass URLs if they exist)
			logo_url:
				posterFilePath || (imgPathList.length > 0 ? imgPathList[0] : null),

			// Blinds (derived from first level of duration list)
			starting_blinds_sb:
				durationList.length > 0 && durationList[0].data[2]
					? durationList[0].data[2]
					: '0',
			starting_blinds_bb:
				durationList.length > 0 && durationList[0].data[3]
					? durationList[0].data[3]
					: '0',
			starting_blinds_ante:
				durationList.length > 0 && durationList[0].data[4]
					? durationList[0].data[4]
					: '0',

			// ... add other necessary fields
		}
		localStorage.setItem('previewEventData', JSON.stringify(eventData))
		window.open('/event/timer-preview', '_blank')
	}

	const btnList = [
		{
			label: '미리보기',
			clickEvent: () => previewEvent(),
			deactive: false,
		},
		{
			label: '수정',
			clickEvent: () => putEvent(),
			deactive: false,
		},
	]
	const adminEventTypeList = [
		{
			flag: 1,
			name: '공식 대회',
		},
		{
			flag: 4,
			name: '트라이아웃 대회',
		},
		{
			flag: 5,
			name: '프리롤 대회',
		},
	]
	const managerEventTypeList = [
		{
			flag: 2,
			name: '공식 예선전 대회',
		},
		{
			flag: 3,
			name: '매장 대회',
		},
	]

	const eventTypeList = [2, 3].includes(getEventData?.data?.event_type_flag)
		? managerEventTypeList
		: adminEventTypeList

	const examQualificationList =
		eventTypeFlag === TRYOUT
			? qualificationList?.data?.tryout_qualification_list?.map((e) => {
					return {
						flag: e?.idx,
						name: e?.qualification_name,
					}
				}) || []
			: [
					{
						flag: 1,
						name: '공식 대회',
					},
					...(qualificationList?.data?.qualification_list?.map((e) => {
						return {
							flag: e?.idx,
							name: e?.qualification_name,
						}
					}) || []),
				]

	const voucherTypeOptions = [
		{value: 'init', label: '== 선택 ==', disabled: true},
		...(ticketTypes?.data?.ticket_types || [])
			.sort((a, b) => a.idx - b.idx)
			.map((voucher) => ({
				value: voucher.idx.toString(),
				label: voucher.name,
			})),
	]

	// 대회 종류 Select 변경 이벤트
	const changeEventTypeEvent = (e) => {
		const newEventType = parseInt(e.target.value)
		setEventTypeFlag(newEventType)

		if (newEventType === STORE) {
			setRequiredProGradeFlag(1)

			if (!voucherTypeInit && selectedVoucherType === null) {
				setSelectedVoucherType('init')
			}
		} else {
			setRequiredProGradeFlag(null)
			if (proGradeRef.current) {
				proGradeRef.current.value = 'init'
			}
		}
	}

	// 요구 프로 자격 Selet 변경 이벤트
	const changeRequiredProGradeEvent = (e) => {
		setRequiredProGradeFlag(parseInt(e.target.value))
	}

	// 시드권 종류 Select 변경 이벤트
	const changeSeedTicketTypeEvent = (e) => {
		setSeedTicketTypeIdx(e.target.value)
	}

	const handleChangeVoucherType = (e) => {
		const value = e.target.value

		if (value === 'init') {
			setSelectedVoucherType(null)
		} else if (value === '참여권') {
			setSelectedVoucherType('참여권')
		} else {
			setSelectedVoucherType(parseInt(value))
		}
	}

	const getCurrentVoucherValue = () => {
		if (!voucherTypeInit) return 'init'

		if (selectedVoucherType === null || selectedVoucherType === undefined) {
			return 'init'
		}

		if (typeof selectedVoucherType === 'string') {
			return selectedVoucherType
		}

		return selectedVoucherType.toString()
	}

	const isStoreEvent = eventTypeFlag === STORE

	const shouldShowVoucherType =
		roleCookie === ROLE_STORE_MANAGER || roleCookie === ROLE_FRANCHISE_MANAGER

	const shouldShowProGrade = !isStoreEvent

	let levelIndex = 0

	return (
		<>
			{isSeriesModalOpen && (
				<SeriesModal
					closeEvent={() => setIsSeriesModalOpen(false)}
					setSeriesData={setSeriesData}
				/>
			)}

			<Style.Wrapper>
				<MainHeader btnList={btnList} />

				{/* 대회 정보 입력 */}
				<ITStyle.TableDiv>
					<ITStyle.TableVerticalElement>
						<h2>
							대회 이름<span>*</span>
						</h2>
						<input
							ref={nameRef}
							placeholder='40글자 이하'
							defaultValue={getEventData?.data?.name}
						/>
					</ITStyle.TableVerticalElement>
					{[OFFICIAL, TRYOUT, FREEROLL].includes(eventTypeFlag) && (
						<ITStyle.TableVerticalElement>
							<h2>
								대회 주소지<span>*</span>
							</h2>
							<input
								ref={addressRef}
								defaultValue={getEventData?.data?.address}
							/>
						</ITStyle.TableVerticalElement>
					)}
					<ITStyle.TableElement>
						<h2>
							대회 종류<span>*</span>
						</h2>
						<select ref={eventTypeRef} onChange={changeEventTypeEvent}>
							<option>== 선택 ==</option>
							{eventTypeList?.map((elem, index) => (
								<option key={index} value={elem?.flag}>
									{elem?.name}
								</option>
							))}
						</select>
					</ITStyle.TableElement>

					{shouldShowProGrade && (
						<ITStyle.TableElement>
							<h2>
								요구 프로 자격<span>*</span>
							</h2>
							<select ref={proGradeRef} onChange={changeRequiredProGradeEvent}>
								<option value='init'>== 선택 ==</option>
								{examQualificationList?.map((elem, index) => (
									<option key={index} value={elem?.flag}>
										{elem?.name}
									</option>
								))}
							</select>
						</ITStyle.TableElement>
					)}

					<ITStyle.TableElement>
						<h2>
							접수 시간<span>*</span>
						</h2>
						<input
							type='datetime-local'
							ref={regDateRef}
							defaultValue={datetimeLocalValueCalculator(
								getEventData?.data?.reg_date
							)}
						/>
					</ITStyle.TableElement>
					<ITStyle.TableElement>
						<h2>
							대회 시간<span>*</span>
						</h2>
						<input
							type='datetime-local'
							ref={dateRef}
							defaultValue={datetimeLocalValueCalculator(
								getEventData?.data?.date
							)}
						/>
					</ITStyle.TableElement>
					<ITStyle.TableVerticalElement>
						<h2>
							본문<span>*</span>
						</h2>
						<textarea
							ref={contentRef}
							defaultValue={getEventData?.data?.content}
						/>
					</ITStyle.TableVerticalElement>
				</ITStyle.TableDiv>

				{/* 대회 포스터 이미지 첨부 */}
				<Style.AreaDiv>
					<h2>대회 포스터 이미지</h2>
					<CStyle.GuideDiv>
						<p>jpg, png 이미지 | 각 10MB 제한</p>
						<p>
							9:16 비율의 이미지에 가장 최적화 되어 있습니다만, 세로로 긴
							이미지라면 크게 상관 없습니다.
						</p>
					</CStyle.GuideDiv>
					<DefaultButton
						small
						fitWidth
						clickEvent={() => posterInputRef.current.click()}
					>
						첨부하기
					</DefaultButton>
					<input
						type='file'
						ref={posterInputRef}
						onChange={uploadPosterEvent}
						style={{display: 'none'}}
					/>

					{posterFilePath && (
						<Style.ImgUploadDiv>
							<Style.ImgElement>
								<button onClick={deletePosterImg}>삭제</button>
								<img
									src={posterFilePath}
									onClick={() => window.open(posterFilePath, '_blank')}
									alt='poster'
								/>
							</Style.ImgElement>
						</Style.ImgUploadDiv>
					)}
				</Style.AreaDiv>

				{/* 대회 이미지 첨부 */}
				<Style.AreaDiv>
					<h2> 이미지 첨부</h2>
					<CStyle.GuideDiv>
						<p>jpg, png 이미지 | 각 10MB 제한 | 최대 10개 까지 첨부</p>
						<p>16:9 비율의 이미지에 가장 최적화 되어 있습니다.</p>
					</CStyle.GuideDiv>
					<DefaultButton
						small
						fitWidth
						clickEvent={() => imgInputRef.current.click()}
					>
						첨부하기
					</DefaultButton>
					<input
						type='file'
						ref={imgInputRef}
						onChange={uploadImgEvent}
						style={{display: 'none'}}
					/>

					{imgPathList.length > 0 && (
						<Style.ImgUploadDiv>
							{imgPathList?.map((elem, index) => (
								<Style.ImgElement $isBanner key={index}>
									<button onClick={() => deleteImg(index)}>삭제</button>
									<img
										src={elem}
										onClick={() => window.open(elem, '_blank')}
										alt='img'
									/>
								</Style.ImgElement>
							))}
						</Style.ImgUploadDiv>
					)}
				</Style.AreaDiv>

				{/* 시리즈 정보 입력 */}
				{[OFFICIAL, PRELIMINARY].includes(eventTypeFlag) && (
					<Style.AreaDiv>
						<Style.AreaHeader>
							<h2>
								대회 시리즈 선택<span>*</span>
							</h2>
							<DefaultButton
								small
								fitWidth
								clickEvent={() => setIsSeriesModalOpen(true)}
							>
								대회 시리즈 검색
							</DefaultButton>
						</Style.AreaHeader>
						{sereisData && (
							<Style.SeriesDataDiv>
								<p>대회 시리즈 IDX : {sereisData?.idx}</p>
								<p>대회 시리즈 이름 : {sereisData?.name}</p>
								<p>
									대회 시리즈 시작 날짜 :{' '}
									{timestampCalculator(sereisData?.start_date, 'date')}
								</p>
								<p>
									대회 시리즈 종료 날짜 :{' '}
									{timestampCalculator(sereisData?.end_date, 'date')}
								</p>
							</Style.SeriesDataDiv>
						)}
					</Style.AreaDiv>
				)}

				{/* 게임 정보 입력 */}
				<Style.AreaDiv>
					<h2>게임 정보 입력</h2>
					<CStyle.GuideDiv>
						<p>
							게임 정보는 사용자 앱에서 출력되는 데이터이자, 듀레이션 타이머의
							자동 계산에 사용됩니다.
						</p>
					</CStyle.GuideDiv>
					<CStyle.GuideDiv>
						<p>
							레지 마감 레벨, 최대 엔트리, 리엔트리 횟수 제한은 입력하지 않을시
							제한 없음으로 동작합니다.
						</p>
						<p>얼리 마감 레벨은 입력하지 않을시 얼리 없음으로 동작합니다.</p>
						<p>
							리엔트리 추가 칩, 얼리 추가 칩, 에드온 추가 칩은 입력하지 않을시
							추가 칩 없음으로 동작합니다.
						</p>
					</CStyle.GuideDiv>
					<CStyle.GuideDiv>
						<p>리엔트리 횟수 제한을 0으로 입력하면 프리즈 아웃이 됩니다.</p>
						<p>
							레지 마감 레벨, 얼리 마감 레벨은 입력한 레벨 전 까지로 동작합니다.
							( ex. 13 입력시 13 레벨이 되는 순간 중단 )
						</p>
					</CStyle.GuideDiv>
					<ITStyle.TableDiv>
						<ITStyle.TableElement>
							<h2>
								스타팅 칩<span>*</span>
							</h2>
							<input
								ref={startingChipRef}
								placeholder='1이상 숫자'
								defaultValue={getEventData?.data?.starting_chip}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>레지 마감 레벨</h2>
							<input
								ref={regEndLevelRef}
								placeholder='1이상 숫자'
								defaultValue={getEventData?.data?.reg_end_level}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>얼리 마감 레벨</h2>
							<input
								ref={earlyEndLevelRef}
								placeholder='1이상 숫자'
								defaultValue={getEventData?.data?.early_end_level}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>얼리 칩</h2>
							<input
								ref={earlyChipRef}
								placeholder='0이상 숫자'
								defaultValue={getEventData?.data?.early_chip}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>에드온 조건</h2>
							<input
								ref={addOnRef}
								placeholder='40글자 이하'
								defaultValue={getEventData?.data?.add_on}
								onKeyDown={handleTextOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>에드온 칩</h2>
							<input
								ref={addOnChipRef}
								placeholder='0이상 숫자'
								defaultValue={getEventData?.data?.add_on_chip}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableVerticalElement>
							<h2>리엔트리 횟수 제한</h2>
							<input
								ref={reEntryRef}
								placeholder='0이상 숫자'
								defaultValue={getEventData?.data?.re_entry}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableVerticalElement>
						<ITStyle.TableVerticalElement>
							<h2>
								리엔트리 칩
								<Style.AddBtn onClick={incReEntryChipEvent}>
									<img src={addIcon} alt='add' />
								</Style.AddBtn>
							</h2>
							{reEntryList?.map((elem, index) => (
								<>
									<input
										onChange={(e) => changeReEntryChipEvent(e, index)}
										placeholder='0이상 숫자'
										defaultValue={elem}
										onKeyDown={handleNumbersOnly}
									/>
									<Style.AddBtn onClick={() => deleteReEntryEvent(index)}>
										<img src={eraseIcon} alt='erase' />
									</Style.AddBtn>
								</>
							))}
						</ITStyle.TableVerticalElement>
						<ITStyle.TableElement>
							<h2>최대 엔트리</h2>
							<input
								ref={maxEntryRef}
								placeholder='1이상 숫자'
								defaultValue={getEventData?.data?.max_entry}
								onKeyDown={handleNumbersOnly}
							/>
						</ITStyle.TableElement>
						<ITStyle.TableElement>
							<h2>총 프라이즈</h2>
							<input
								ref={prizeTotalRef}
								placeholder='40글자 이하'
								defaultValue={getEventData?.data?.prize_total}
							/>
						</ITStyle.TableElement>

						{sereisData && eventTypeFlag === OFFICIAL && (
							<>
								<ITStyle.TableElement>
									<h2>
										참가 시드권 종류<span>*</span>
									</h2>
									<select
										ref={seeTicketTypeRef}
										onChange={changeSeedTicketTypeEvent}
									>
										<option>== 선택 ==</option>
										{seedTicketTypeList?.data?.seed_ticket_type?.map(
											(elem, index) => (
												<option key={index} value={elem?.idx}>
													{elem?.name}
												</option>
											)
										)}
									</select>
								</ITStyle.TableElement>
								<ITStyle.TableElement>
									<h2>
										참가 시드권 개수<span>*</span>
									</h2>
									<input
										ref={ticketNumberRef}
										placeholder='1이상 숫자'
										defaultValue={getEventData?.data?.ticket_number}
										onKeyDown={handleNumbersOnly}
									/>
								</ITStyle.TableElement>
							</>
						)}
						{[STORE, PRELIMINARY].includes(eventTypeFlag) && (
							<ITStyle.TableElement>
								<h2>
									참가 매장 이용권 개수<span>*</span>
								</h2>
								<input
									ref={ticketNumberRef}
									placeholder='1이상 숫자'
									defaultValue={getEventData?.data?.ticket_number}
									onKeyDown={handleNumbersOnly}
								/>
							</ITStyle.TableElement>
						)}
						{eventTypeFlag === FREEROLL && (
							<ITStyle.TableElement>
								<h2>
									참가권 발행 개수<span>*</span>
								</h2>
								<input
									ref={publishedTicketNumberRef}
									placeholder='1이상 숫자'
									defaultValue={getEventData?.data?.published_ticket_number}
									onKeyDown={handleNumbersOnly}
								/>
							</ITStyle.TableElement>
						)}

						{/* 매장 이용권 유형 */}
						{shouldShowVoucherType && (
							<ITStyle.TableElement>
								<h2>
									매장 이용권 유형<span>*</span>
								</h2>
								<select
									ref={voucherTypeRef}
									onChange={handleChangeVoucherType}
									value={getCurrentVoucherValue()}
								>
									{voucherTypeOptions.map((option, index) => (
										<option
											key={index}
											value={option?.value}
											disabled={option?.disabled}
										>
											{option?.label}
										</option>
									))}
								</select>
							</ITStyle.TableElement>
						)}
					</ITStyle.TableDiv>
				</Style.AreaDiv>

				{/* 프라이즈 정보 입력 */}
				<Style.AreaDiv>
					<Style.AreaHeader>
						<h2>
							프라이즈 목록<span>*</span>
						</h2>
						<DefaultButton small fitWidth clickEvent={createPrizeEvent}>
							구간 추가
						</DefaultButton>
					</Style.AreaHeader>
					<CStyle.GuideDiv>
						<p>
							프라이즈는 출력용 데이터이므로 자유로운 양식으로 작성해도 됩니다.
						</p>
					</CStyle.GuideDiv>

					<Style.PrizeTable>
						<Style.TableHeader>
							<Style.RankTd>순위 정보</Style.RankTd>
							<td>프라이즈 정보</td>
							<td></td>
						</Style.TableHeader>
						{prizeListTable.map((elem, index) => (
							<tr key={index}>
								{elem?.map((childElem, childIndex) => (
									<Style.ElemTd key={childIndex}>
										<input
											onChange={(e) => setPrize(e, childIndex, index)}
											value={childElem}
										/>
									</Style.ElemTd>
								))}
								<Style.FitTd>
									<Style.ControlDiv>
										<button onClick={() => deletePrizeEvent(index)}>
											<img src={eraseIcon} alt='erase' />
										</button>
										<button onClick={() => upperPrizeEvent(index)}>
											<img src={upperIcon} alt='upper' />
										</button>
										<button onClick={() => downPrizeEvent(index)}>
											<img src={downIcon} alt='down' />
										</button>
									</Style.ControlDiv>
								</Style.FitTd>
							</tr>
						))}
					</Style.PrizeTable>
				</Style.AreaDiv>

				{/* 프라이즈 정보 입력 */}
				<Style.AreaDiv>
					<Style.AreaHeader>
						<h2>
							프라이즈 목록<span>*</span>
						</h2>
						<PrizeHeaderControls
							durationRef={durationRef}
							onKeyDown={handleNumbersOnly}
							eventData={getEventData?.data}
							onAddPrize={addPrize}
						/>
					</Style.AreaHeader>
					<CStyle.GuideDiv>
						<p>
							프라이즈는 출력용 데이터이므로 자유로운 양식으로 작성해도 됩니다.
						</p>
					</CStyle.GuideDiv>

					<PrizeContainer>
						{prizes.map((prize, index) => (
							<Prize
								key={prize.id}
								index={index}
								onRemove={
									prizes.length > 1 ? () => removePrize(prize.id) : undefined
								}
								showRemoveButton={prizes.length > 1}
							>
								<TiptapEditor
									key={prize.id}
									ref={getPrizeEditorRef(prize.id)}
									initialContent={prize.content}
									placeholder={`프라이즈 ${index + 1} 정보를 입력하세요...`}
									onUpdate={(params) => {
										updatePrizeContent(prize.id, params.editor.getHTML())
									}}
								/>
							</Prize>
						))}
					</PrizeContainer>
				</Style.AreaDiv>

				{/* 듀레이션 정보 입력 */}
				<Style.AreaDiv>
					<Style.AreaHeader>
						<h2>
							듀레이션 목록<span>*</span>
						</h2>
						<div />
					</Style.AreaHeader>
					<CStyle.GuideDiv>
						<p>모든 값은 숫자로만 입력해야 합니다.</p>
						<p>
							구간 타임 추가 버튼을 누르면 목록 하단에 영역이 추가되고 값을
							입력할 수 있습니다.
						</p>
						<p>
							브레이크 타임 추가 버튼을 누르면 목록 하단에 브레이크 영역이
							추가되고 Minute만 입력할 수 있습니다.
						</p>
						<p>SB를 입력하면 자동으로 BB와 Ante에 2배 값이 입력됩니다.</p>
						<p>BB를 입력하면 자동으로 Ante에 같은 값이 입력됩니다.</p>
					</CStyle.GuideDiv>

					<Style.PrizeTable>
						<thead>
							<Style.TableHeader>
								<td>Level</td>
								<td>SB</td>
								<td>BB</td>
								<td>Ante</td>
								<td>Minute</td>
								<td></td>
								<td></td>
							</Style.TableHeader>
						</thead>
						<DndContext
							collisionDetection={closestCenter}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={durationList.map((item) => item.id)}
								strategy={verticalListSortingStrategy}
							>
								{durationList.map((item, index) => {
									const elem = item.data
									if (elem[0] !== 'break') levelIndex += 1
									const currentLevel = levelIndex

									return (
										<SortableRow key={item.id} id={item.id}>
											{(listeners) => (
												<>
													{[0, 2, 3, 4, 1].map((childIndex) => {
														const childElem = elem[childIndex]
														return (
															<Style.ElemTd
																$head={childIndex === 0}
																key={childIndex}
															>
																{childElem !== 'empty' ? (
																	<input
																		onChange={(e) =>
																			setDuration(e, childIndex, index)
																		}
																		value={
																			childIndex === 0 && childElem !== 'break'
																				? `Lv.${currentLevel}`
																				: childElem
																		}
																		readOnly={childIndex === 0}
																	/>
																) : null}
															</Style.ElemTd>
														)
													})}
													<Style.MoveTd>
														<Style.ControlDiv>
															<button
																onClick={() => deleteDurationEvent(index)}
															>
																<img src={eraseIcon} alt='erase' />
															</button>
															<div
																{...listeners}
																style={{
																	cursor: 'grab',
																	display: 'flex',
																	alignItems: 'center',
																	width: 'fit-content',
																}}
															>
																<img src={dragIcon} alt='drag' />
															</div>
														</Style.ControlDiv>
													</Style.MoveTd>
													<Style.ActionTd>
														<DefaultButton
															small
															fitWidth
															clickEvent={(e) => {
																e.stopPropagation()
																if (isDraggingRef.current) return
																insertDurationEvent(index)
															}}
														>
															구간 추가
														</DefaultButton>
														<DefaultButton
															small
															fitWidth
															clickEvent={(e) => {
																e.stopPropagation()
																if (isDraggingRef.current) return
																insertBreakEvent(index)
															}}
														>
															브레이크 타임 추가
														</DefaultButton>
													</Style.ActionTd>
												</>
											)}
										</SortableRow>
									)
								})}
							</SortableContext>
						</DndContext>
					</Style.PrizeTable>
				</Style.AreaDiv>
			</Style.Wrapper>
		</>
	)
}

export default EventEdit
